/**
 * 统一状态编辑组件 - 行内即时保存版本
 * 轻量显示原值和新值，点击即时保存
 *
 * 修复内容：
 * 1. 新增数据的原值显示为 "-"
 * 2. 正确显示原值和新值的对比
 * 3. 新增数据默认状态为 "PENDING"，用户可修改
 */

import React, { useEffect, useState, useCallback } from "react";
import { Tag, Popover, message, Button, Space } from "antd";
import { EditOutlined, LoadingOutlined } from "@ant-design/icons";
import TransactionStatusTag from "../../Monitor/components/TransactionStatusTag";
import { useBatchTransactionContext } from "../context/BatchTransactionContext";
import { TRANSACTION_STATUS } from "../types";

/**
 * 统一状态编辑组件
 * @param {Object} record - 记录对象（事务或明细）
 * @param {string} transactionType - 事务类型
 * @param {string} transactionId - 事务ID（明细编辑时需要）
 * @param {boolean} disabled - 是否禁用编辑
 * @param {boolean} isDetail - 是否为明细记录
 * @param {Array} statusOptions - 状态选项数组，格式：[{value, label, color}]
 */
const UnifiedStatusEditor = ({ record, transactionType, transactionId, disabled = false, isDetail = false, statusOptions = [] }) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const { transactionManager } = useBatchTransactionContext();

    // 检查是否为新记录
    const isNewRecord = record?.clientStatus === "new" || record?.isNewTransaction;

    // 获取原始状态和当前状态
    let originalStatus, currentStatus;

    if (isNewRecord) {
        // 新增记录：原值为 null（显示为"-"），当前值默认为 PENDING 或用户设置的值
        originalStatus = null;
        // 对于明细记录，使用 currentStatus 字段；对于事务记录，使用 status 字段
        currentStatus = isDetail ? record?.currentStatus || TRANSACTION_STATUS.PENDING : record?.status || TRANSACTION_STATUS.PENDING;
    } else {
        // 已存在记录：原值从 originalData 获取，当前值为用户可能修改的值
        if (isDetail) {
            originalStatus = record?.originalData?.currentStatus || null;
            currentStatus = record?.currentStatus || null;
        } else {
            originalStatus = record?.originalData?.status || null;
            currentStatus = record?.status || null;
        }
    }

    // 任何时候都可以编辑（包括新增记录）
    const canEdit = !disabled;

    // 处理状态变更 - 即时保存
    const handleStatusChange = useCallback(
        async (newStatus) => {
            if (saving) return;

            try {
                setSaving(true);

                if (isDetail) {
                    // 明细状态更新 - 使用 currentStatus 字段
                    await transactionManager.updateDetail(transactionId, record.id, { currentStatus: newStatus });
                } else {
                    // 事务状态更新 - 使用 status 字段
                    await transactionManager.updateTransaction(record.id, { status: newStatus });
                }

                message.success("状态已更新");
                setPopoverVisible(false);
            } catch (error) {
                console.error("状态更新失败:", error);
                message.error("状态更新失败: " + (error.message || "未知错误"));
            } finally {
                setSaving(false);
            }
        },
        [saving, isDetail, transactionId, record.id, transactionManager],
    );

    // 确保新增记录有默认状态
    React.useEffect(() => {
        if (isNewRecord && !currentStatus) {
            // 为新增记录设置默认状态 PENDING
            if (isDetail) {
                transactionManager.updateDetail(transactionId, record.id, { currentStatus: TRANSACTION_STATUS.PENDING });
            } else {
                transactionManager.updateTransaction(record.id, { status: TRANSACTION_STATUS.PENDING });
            }
        }
    }, [isNewRecord, currentStatus, isDetail, transactionId, record.id, transactionManager]);

    // statusOptions 现在由父组件传入，不再在组件内部生成

    // 状态选择器内容
    const statusSelector = (
        <div style={{ minWidth: 200, maxWidth: 300 }}>
            <div style={{ marginBottom: 12, fontSize: 12, color: "#666" }}>{isNewRecord ? "设置初始状态：" : "修改状态："}</div>

            {/* 平铺的状态选项 */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {statusOptions.map((opt) => (
                    <Button
                        key={opt.value}
                        type={currentStatus === opt.value ? "primary" : "default"}
                        size="small"
                        loading={saving && currentStatus === opt.value}
                        disabled={saving}
                        onClick={() => handleStatusChange(opt.value)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            width: "100%",
                            height: "auto",
                            padding: "6px 12px",
                            textAlign: "left",
                            border: "none",
                            backgroundColor: currentStatus === opt.value ? `${opt.color}08` : "transparent",
                            boxShadow: "none",
                        }}
                    >
                        <Tag
                            color={opt.color}
                            size="small"
                            style={{
                                width: "100%",
                                margin: 0,
                                marginRight: 8,
                                fontWeight: currentStatus === opt.value ? "500" : "normal",
                                borderWidth: currentStatus === opt.value ? "2px" : "1px",
                            }}
                        >
                            {opt.label}
                        </Tag>
                        {saving && currentStatus === opt.value && <LoadingOutlined style={{ marginLeft: "auto", color: opt.color }} />}
                    </Button>
                ))}
            </div>

            {/* 状态对比显示 */}
            <div style={{ marginTop: 12, fontSize: 11, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
                {isNewRecord ? (
                    // 新增记录：显示原值为"-"，新值为当前选择的状态
                    <div>
                        <div style={{ color: "#999", marginBottom: 4 }}>
                            原值: <span style={{ color: "#999" }}>-</span>
                        </div>
                        {currentStatus && (
                            <div style={{ color: "#666" }}>
                                新值: <TransactionStatusTag status={currentStatus} type={transactionType} size="small" />
                            </div>
                        )}
                    </div>
                ) : (
                    // 已存在记录：显示原值和新值的对比
                    <div>
                        <div style={{ color: "#999", marginBottom: 4 }}>原值: {originalStatus ? <TransactionStatusTag status={originalStatus} type={transactionType} size="small" /> : <span style={{ color: "#999" }}>-</span>}</div>
                        {currentStatus && currentStatus !== originalStatus && (
                            <div style={{ color: "#666" }}>
                                新值: <TransactionStatusTag status={currentStatus} type={transactionType} size="small" />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Popover content={statusSelector} title="编辑状态" trigger="click" open={popoverVisible} onOpenChange={setPopoverVisible} disabled={!canEdit}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                    cursor: canEdit ? "pointer" : "not-allowed",
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "background-color 0.2s",
                    opacity: canEdit ? 1 : 0.6,
                    position: "relative",
                }}
                onMouseEnter={(e) => {
                    if (canEdit) {
                        e.currentTarget.style.backgroundColor = "#f5f5f5";
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                }}
            >
                {/* 统一的状态显示：旧值 → 新值 */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {/* 旧值 */}
                    <TransactionStatusTag status={isNewRecord ? null : originalStatus} type={transactionType} size="small" showIcon={false} textOnly={true} />

                    {/* 箭头（仅当有变更或新增时显示） */}
                    {(isNewRecord || (currentStatus && currentStatus !== originalStatus)) && <span style={{ color: "#666", fontSize: "12px" }}>→</span>}

                    {/* 新值 */}
                    {isNewRecord || (currentStatus && currentStatus !== originalStatus) ? (
                        <TransactionStatusTag status={currentStatus} type={transactionType} size="small" showIcon={false} textOnly={true} />
                    ) : (
                        // 没有变更时显示编辑图标
                        canEdit && (
                            <EditOutlined
                                style={{
                                    fontSize: "12px",
                                    color: "#1890ff",
                                    opacity: 0.7,
                                }}
                            />
                        )
                    )}
                </div>

                {/* 新增记录标识 （暂时不需要）因为使用组件的table中的行中有其他标识来标记是否新增 */}
                {/* {isNewRecord && (
                    <Tag size="small" color="blue" style={{ fontSize: "10px", marginTop: "2px" }}>
                        {isDetail ? "新增明细" : "新增事务"}
                    </Tag>
                )} */}
            </div>
        </Popover>
    );
};

export default UnifiedStatusEditor;
